/**
 * Commission Batches Management
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Users, Loader2, ChevronLeft, ChevronRight, Send, CheckCircle, XCircle, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString() : '—';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600' },
  pending_approval: { label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-700' }
};

const StatusChip = ({ status }) => {
  const config = STATUS_CONFIG[status] || { label: status, color: 'bg-slate-100 text-slate-600' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
};

export default function CommissionBatches({ user }) {
  const [batches, setBatches] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', skip: 0, limit: 25 });

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      params.append('skip', filters.skip);
      params.append('limit', filters.limit);

      const response = await axios.get(`${API}/finance/hub/commissions?${params}`);
      setBatches(response.data.items);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Error fetching batches:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const handleSubmit = async (batchId) => {
    if (!window.confirm('Submit this batch for approval?')) return;
    try {
      await axios.post(`${API}/finance/commissions/batches/${batchId}/submit`, {});
      toast.success('Batch submitted for approval');
      fetchBatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit batch');
    }
  };

  const handleApprove = async (batchId) => {
    if (!window.confirm('Approve this commission batch?')) return;
    try {
      await axios.post(`${API}/finance/commissions/batches/${batchId}/approve`, {});
      toast.success('Batch approved');
      fetchBatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve batch');
    }
  };

  const handleReject = async (batchId) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    try {
      await axios.post(`${API}/finance/commissions/batches/${batchId}/reject`, { reason });
      toast.success('Batch rejected');
      fetchBatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject batch');
    }
  };

  const handleMarkPaid = async (batchId) => {
    if (!window.confirm('Mark this batch as paid?')) return;
    try {
      await axios.post(`${API}/finance/commissions/batches/${batchId}/mark-paid`, {});
      toast.success('Batch marked as paid');
      fetchBatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark batch as paid');
    }
  };

  const totalPages = Math.ceil(total / filters.limit);
  const currentPage = Math.floor(filters.skip / filters.limit) + 1;

  return (
    <Card data-testid="commission-batches">
      <CardHeader className="flex flex-row items-center justify-between py-4">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Users className="h-4 w-4 text-purple-500" />
          Commission Batches ({total})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex gap-3 mb-4">
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v, skip: 0 })}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending_approval">Pending Approval</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchBatches}>Refresh</Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : batches.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Batch #</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-center">Records</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[150px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map((batch) => (
                    <TableRow key={batch.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <TableCell className="font-medium">{batch.batch_number || '—'}</TableCell>
                      <TableCell>{batch.name || '—'}</TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {formatDate(batch.period_start)} - {formatDate(batch.period_end)}
                      </TableCell>
                      <TableCell className="text-center">{batch.total_records}</TableCell>
                      <TableCell className="text-right">{formatCurrency(batch.total_gross)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(batch.total_net)}</TableCell>
                      <TableCell><StatusChip status={batch.status} /></TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {batch.status === 'draft' && batch.total_records > 0 && (
                            <Button size="sm" variant="outline" onClick={() => handleSubmit(batch.id)} title="Submit for approval">
                              <Send className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {batch.status === 'pending_approval' && (
                            <>
                              <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleApprove(batch.id)} title="Approve">
                                <CheckCircle className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleReject(batch.id)} title="Reject">
                                <XCircle className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                          {batch.status === 'approved' && (
                            <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => handleMarkPaid(batch.id)} title="Mark Paid">
                              <DollarSign className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-slate-500">
                Showing {filters.skip + 1}-{Math.min(filters.skip + filters.limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={currentPage === 1}
                  onClick={() => setFilters({ ...filters, skip: filters.skip - filters.limit })}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-3 py-1 text-sm">Page {currentPage} of {totalPages || 1}</span>
                <Button variant="outline" size="sm" disabled={currentPage >= totalPages}
                  onClick={() => setFilters({ ...filters, skip: filters.skip + filters.limit })}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-slate-500">No commission batches found</div>
        )}
      </CardContent>
    </Card>
  );
}
